/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import type { IconButtonProps } from '@atlaskit/button/new';
import { jsx } from '@atlaskit/css';
import CopyIcon from '@atlaskit/icon/core/copy';

import messages from './messages';

type Props = {
	appearance?: IconButtonProps['appearance'];
	onClick: (event: React.MouseEvent) => void;
	spacing?: IconButtonProps['spacing'];
	tabIndex?: number;
};

export const CopyIconButton = ({ onClick, tabIndex, appearance, spacing }: Props) => {
	const { formatMessage } = useIntl();
	const [tooltip, setTooltip] = React.useState(formatMessage(messages.copy));
	const updateTooltip = React.useRef<() => void>();

	const handleOnClick = React.useCallback(
		(event: React.MouseEvent<HTMLButtonElement | HTMLElement, MouseEvent>) => {
			onClick(event);
			setTooltip(formatMessage(messages.copiedToClipboard));
		},
		[onClick, formatMessage],
	);

	const resetTooltip = React.useCallback(
		() => setTooltip(formatMessage(messages.copy)),
		[formatMessage],
	);

	React.useLayoutEffect(() => {
		updateTooltip.current?.();
	}, [tooltip]);

	const tooltipOptions: IconButtonProps['tooltip'] = {
		position: 'top',
		content: ({ update }) => {
			updateTooltip.current = update;
			return tooltip;
		},
		onHide: resetTooltip,
	};

	return (
		<IconButton
			testId="copy-response-button"
			type="button"
			onClick={handleOnClick}
			icon={CopyIcon}
			label={tooltip}
			appearance={appearance}
			spacing={spacing}
			tabIndex={tabIndex}
			isTooltipDisabled={false}
			tooltip={tooltipOptions}
		/>
	);
};
